import React from "react";
import PropTypes from "prop-types";
import { useHistory, useLocation } from "react-router-dom";
import {
  buildAttendanceRoute,
  getClassIdFromLessonId,
  getLessonIndexFromId,
} from "./attendanceLessonSelectors";
import {
  hasAllParticipantsCancelled,
  isAttendanceChecked,
  isLessonCancelled,
} from "./lessonAttendanceStatus";
import { useTranslation } from 'react-i18next';

const getStudentName = (students, studentRef) => {
  const student = students.find(item => item.id === studentRef.id) || studentRef;
  return `${student.firstName || ""} ${student.lastName || ""}`.trim() || student.id;
};

const getDisplayStudents = (scheduledLesson) => {
  const registerStudents = (scheduledLesson.lessonRegister && scheduledLesson.lessonRegister.students) || [];
  return registerStudents.length > 0 ? registerStudents : scheduledLesson.defaultStudents || [];
};

const getLessonBackgroundClass = (scheduledLesson) => {
  if (isLessonCancelled(scheduledLesson)) return "cancelled-background";
  if (hasAllParticipantsCancelled(scheduledLesson)) return "partially-cancelled-background";
  if (isAttendanceChecked(scheduledLesson)) return "dark-background";
  return "light-background";
};

function LessonWrapper({ event, display, students }) {
  const { t: tr } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { scheduledLesson } = event;
  const lessonId = event.id || scheduledLesson.id;
  const classId = scheduledLesson.schoolClassId || getClassIdFromLessonId(lessonId);
  const lessonIndex = getLessonIndexFromId(lessonId);
  const displayStudents = getDisplayStudents(scheduledLesson);
  const savedDetails = (scheduledLesson.lessonRegister && scheduledLesson.lessonRegister.lessonDetails) || {};
  const hasSavedSummary = typeof savedDetails.lessonSummary === "string" && savedDetails.lessonSummary.trim().length > 0;

  const openAttendance = () => {
    history.push(buildAttendanceRoute(classId, lessonIndex, location.search));
  };

  const handleKeyDown = (eventKey) => {
    if (eventKey.key === "Enter" || eventKey.key === " ") {
      eventKey.preventDefault();
      openAttendance();
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      className={`lesson-attendance-link display-block w-100 cursor-pointer ${display} ${getLessonBackgroundClass(scheduledLesson)}`}
      onClick={openAttendance}
      onKeyDown={handleKeyDown}
    >
      {hasSavedSummary && (
        <span className="lesson-summary-indicator" title={tr("manage:topicAdded_248")}>
          <i className="far fa-file-alt" aria-hidden="true" />
          <span className="sr-only">{tr("manage:topicAdded_248")}</span>
        </span>
      )}
      {displayStudents.length === 0 ? (
        <div className="student-name-display-block empty-lesson-display-block">
          {display === 'calendar-event' ? <>{event.title || tr("common:classes")}<br />{tr("manage:noStudents_249")}</> : <>&nbsp;</>}
        </div>
      ) : (
        displayStudents.map(student => (
          <div key={student.id} className="student-name-display-block">{getStudentName(students, student)}</div>
        ))
      )}
    </div>
  );
}

LessonWrapper.propTypes = {
  event: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string,
    scheduledLesson: PropTypes.shape({
      id: PropTypes.string.isRequired,
      schoolClassId: PropTypes.string,
      defaultStudents: PropTypes.array,
      lessonRegister: PropTypes.shape({
        lessonDetails: PropTypes.shape({ lessonSummary: PropTypes.string }),
        students: PropTypes.array,
      }),
    }).isRequired,
  }).isRequired,
  display: PropTypes.string.isRequired,
  students: PropTypes.array,
};

LessonWrapper.defaultProps = {
  students: [],
};

export default LessonWrapper;
